"use client";

import { ArrowRightIcon } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { Button } from "../button";

export interface MarketingHeroProps {
	badgeLeft: string;
	badgeRight: string;
	title: string;
	lead: string;
	primaryCtaHref: string;
	primaryCtaLabel: string;
	secondaryCtaHref?: string;
	secondaryCtaLabel?: string;
	imageLightSrc: string;
	imageDarkSrc: string;
	imageAlt: string;
}

export function MarketingHero(props: MarketingHeroProps) {
	return (
		<div className="relative max-w-full overflow-x-hidden">
			<div className="container relative z-20 pt-24 pb-12 lg:pb-16">
				<div className="mb-4 flex justify-start">
					<div className="flex flex-wrap items-center justify-start rounded-full bg-muted p-px px-3 py-1 font-normal text-foreground text-sm">
						<span className="flex items-center gap-2 rounded-full font-semibold">
							{props.badgeLeft}
						</span>
						<span className="ml-1 block font-medium">
							{props.badgeRight}
						</span>
					</div>
				</div>

				<h1 className="text-balance font-medium text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-tighter text-foreground">
					{props.title}
				</h1>

				<p className="mt-2 max-w-3xl text-foreground/60 text-sm sm:text-lg">
					{props.lead}
				</p>

				<div className="mt-4 flex items-center justify-start gap-2">
					<Button size="lg" variant="primary" asChild>
						<Link href={props.primaryCtaHref}>
							{props.primaryCtaLabel}
							<ArrowRightIcon className="ml-2 size-4" />
						</Link>
					</Button>

					{props.secondaryCtaHref ? (
						<Button variant="ghost" size="lg" asChild>
							<Link href={props.secondaryCtaHref}>
								{props.secondaryCtaLabel}
							</Link>
						</Button>
					) : null}
				</div>

				<div className="mx-auto mt-12 rounded-4xl border bg-primary/5 p-4 lg:mt-16 xl:mt-24 lg:flex-1">
					<Image
						src={props.imageLightSrc}
						alt={props.imageAlt}
						className="block rounded-xl dark:hidden"
						width={1600}
						height={900}
						priority
					/>
					<Image
						src={props.imageDarkSrc}
						alt={props.imageAlt}
						className="hidden rounded-xl dark:block"
						width={1600}
						height={900}
						priority
					/>
				</div>
			</div>
		</div>
	);
}
